import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { createPortal } from 'react-dom';

import { Pressable, xcss } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import { useJsmPlaygroundSkipLayer } from './jsm-playground-demo-layer';
import { usePrefersReducedMotion } from './jsm-playground-motion';

const SKIP_ANIMATION_CONTROL = 'data-jsm-playground-skip-animation';

/** True when the event came from Skip, so a screen takeover must not swallow that activation. */
export const isJsmPlaygroundSkipAnimationTarget = (target: EventTarget | null) =>
	target instanceof Element && target.closest(`[${SKIP_ANIMATION_CONTROL}]`) !== null;

type JsmPlaygroundSkipAnimationButtonProps = {
	isVisible: boolean;
	label: string;
	onClick: () => void;
};

const SKIP_ANIMATION_FADE_MS = 300;

const buttonStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlock: 'space.100',
	paddingInline: 'space.150',
	backgroundColor: 'elevation.surface',
	color: 'color.text',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	whiteSpace: 'nowrap',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.pressed',
	},
});

const skipAnimationStyles = css({
	position: 'absolute',
	insetBlockEnd: token('space.200'),
	insetInlineStart: token('space.200'),
	insetInlineEnd: 'auto',
	zIndex: 800,
	display: 'flex',
	width: 'fit-content',
	opacity: 1,
	pointerEvents: 'auto',
	transitionProperty: 'opacity',
	transitionDuration: `${SKIP_ANIMATION_FADE_MS}ms`,
	transitionTimingFunction: 'ease-out',
	'@media (prefers-reduced-motion: reduce)': {
		transitionProperty: 'none',
	},
});

const fadingStyles = css({
	opacity: 0,
	pointerEvents: 'none',
});

export const JsmPlaygroundSkipAnimationButton = ({
	isVisible,
	label,
	onClick,
}: JsmPlaygroundSkipAnimationButtonProps) => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const skipLayer = useJsmPlaygroundSkipLayer();
	const [isRendered, setIsRendered] = useState(isVisible);
	const [isFadingOut, setIsFadingOut] = useState(false);

	useEffect(() => {
		if (isVisible) {
			setIsRendered(true);
			setIsFadingOut(false);
			return;
		}
		if (!isRendered) {
			return;
		}
		if (prefersReducedMotion) {
			setIsRendered(false);
			setIsFadingOut(false);
			return;
		}
		setIsFadingOut(true);
		const timeout = window.setTimeout(() => {
			setIsRendered(false);
		}, SKIP_ANIMATION_FADE_MS);
		return () => window.clearTimeout(timeout);
	}, [isRendered, isVisible, prefersReducedMotion]);

	if (!isRendered) {
		return null;
	}

	const control = (
		<div
			data-jsm-playground-skip-animation=""
			css={[skipAnimationStyles, isFadingOut && fadingStyles]}
		>
			<Pressable
				xcss={buttonStyles}
				style={{
					borderRadius: token('radius.full'),
					borderStyle: 'none',
					// elevation.shadow.overlay is a soft drop plus a 1px hairline. The hairline
					// is what still reads as a border after border-style is none.
					// eslint-disable-next-line local/ensure-wac-token-usage
					boxShadow: '0 8px 12px rgba(30, 31, 33, 0.15)',
				}}
				onClick={onClick}
				aria-hidden={isFadingOut || undefined}
				// @ts-expect-error -- inert is valid HTML but is missing from React types
				inert={isFadingOut ? '' : undefined}
			>
				{label}
			</Pressable>
		</div>
	);

	return skipLayer ? createPortal(control, skipLayer) : control;
};
